/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text, TextInput, UnstyledButton } from '@/shared/ui';
import { getSettingsNav, type SettingsNavOptions } from '../constants/settings-nav';
import {
  itemsOf, matchSettings, type SettingsSearchEntry, type SettingsTarget,
} from '../constants/settings-items';

interface SettingsSearchProps {
  navOptions: SettingsNavOptions;
  onNavigate: (target: SettingsTarget) => void;
}

/**
 * Everything the user can reach from the search: each visible tab, and each
 * setting listed inside one.
 *
 * @param t - The settings translator.
 * @param navOptions - The gates that hide tabs.
 * @returns The search entries, tabs first.
 */
function buildEntries(t: TFunction, navOptions: SettingsNavOptions): SettingsSearchEntry[] {
  const sections = getSettingsNav(t, navOptions);
  const tabEntries = sections.map((section) => ({
    target: { section: section.key, item: null },
    title: section.label,
    context: t(`v2.nav.summaries.${section.key}`),
    keywords: t(`v2.search.keywords.${section.key}`, { defaultValue: '' }),
  }));
  const itemEntries = sections.flatMap((section) => itemsOf(section.key).map((item) => ({
    target: { section: section.key, item },
    title: t(`v2.items.${item}.title`),
    context: section.label,
    keywords: t(`v2.items.${item}.keywords`, { defaultValue: '' }),
  })));
  return [...itemEntries, ...tabEntries];
}

/**
 * "Procurar configuração": finds a tab or a setting by its name or by the
 * words people use for it, and opens it directly. The settings page keeps
 * growing; the search is what keeps the newest setting one word away.
 *
 * @returns The search field with its results.
 */
export function SettingsSearch({ navOptions, onNavigate }: SettingsSearchProps) {
  const { t } = useTranslation('features/agency-settings');
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const entries = useMemo(() => buildEntries(t, navOptions), [t, navOptions]);
  const results = matchSettings(entries, query);
  const showResults = focused && query.trim() !== '';

  const open = (entry: SettingsSearchEntry) => {
    setQuery('');
    onNavigate(entry.target);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') setQuery('');
    if (event.key === 'Enter' && results[0]) open(results[0]);
  };

  return (
    <div role="search" className="relative" style={{ flex: '1 1 320px', minWidth: 260, maxWidth: 360 }}>
      <TextInput
        name="settings-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={handleKeyDown}
        placeholder={t('v2.search.placeholder')}
        aria-label={t('v2.search.label')}
        aria-expanded={showResults}
        aria-controls="settings-search-results"
        autoComplete="off"
        data-testid="settings-search-input"
      />
      {showResults && (
        <div
          id="settings-search-results"
          data-testid="settings-search-results"
          className="absolute right-0 left-0 z-20"
          style={{
            top: 'calc(100% + var(--space-1))', padding: 'var(--space-1-5)',
            border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)',
            background: 'var(--surface-raised)', boxShadow: 'var(--shadow-md)',
          }}
          // Keeps the input focused while a result is clicked, so the blur
          // does not close the list before the click lands.
          onMouseDown={(e) => e.preventDefault()}
        >
          {results.length === 0 && (
            <Text style={{ margin: 0, padding: 'var(--space-2-5) var(--space-3)', fontSize: 'var(--text-small)', color: 'var(--text-tertiary-soft)' }}>
              {t('v2.search.empty')}
            </Text>
          )}
          {results.map((entry) => (
            <UnstyledButton
              key={`${entry.target.section}-${entry.target.item ?? ''}`}
              type="button"
              onClick={() => open(entry)}
              className="flex w-full flex-col items-start rounded-(--radius-md) text-left hover:bg-(--surface-hover)"
              style={{ gap: 2, padding: 'var(--space-2-5) var(--space-3)', fontFamily: 'var(--font-family)' }}
              data-testid={`settings-search-result-${entry.target.item ?? entry.target.section}`}
            >
              <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
                {entry.title}
              </Text>
              <Text component="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary-soft)' }}>
                {entry.context}
              </Text>
            </UnstyledButton>
          ))}
        </div>
      )}
    </div>
  );
}
